import { Box, Button, Field, Input, Stack, Text, VStack } from "@chakra-ui/react"
import { useState, useCallback } from "react"
import { authClient } from "./auth"
import Link from "@hydrologiq/shared/components/Link"

export function ForgotPasswordForm() {
  const [email, setEmail] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [loading, setLoading] = useState(false)

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      setError(null)
      setSuccess(false)
      setLoading(true)

      if (!email) {
        setError("Email is required")
        setLoading(false)
        return
      }

      try {
        await authClient.requestPasswordReset({
          email,
          redirectTo: "/auth/reset-password",
        })

        setSuccess(true)
        setEmail("")
      } catch (err: any) {
        // Better Auth may return success even if email doesn't exist (security best practice)
        // So we'll always show success to prevent email enumeration
        setSuccess(true)
        setEmail("")
      } finally {
        setLoading(false)
      }
    },
    [email]
  )

  return (
    <Box
      as="form"
      onSubmit={handleSubmit}
      w="full"
      maxW="md"
      bg="card"
      p={8}
      borderRadius="md"
      boxShadow="md"
      color="primary"
    >
      <VStack gap={6} align="stretch">
        <VStack gap={2} align="stretch">
          <Text fontSize="2xl" fontWeight="bold">
            Reset Password
          </Text>
          <Text fontSize="sm" color="primary_light">
            Enter your email address and we'll send you a link to reset your password.
          </Text>
        </VStack>

        {success ? (
          <VStack gap={4} align="stretch">
            <Box bg="green.50" p={4} borderRadius="md" borderWidth="1px" borderColor="green.200">
              <Text color="green.700" fontSize="sm">
                If an account exists with that email, you will receive a password reset link shortly.
              </Text>
            </Box>
            <Link href="/auth/sign-in" fontSize="sm" variant={"primary" as any} internal>
              Back to Sign In
            </Link>
          </VStack>
        ) : (
          <>
            <Field.Root required>
              <Field.Label>Email</Field.Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                disabled={loading}
              />
            </Field.Root>

            {error && (
              <Text color="error" fontSize="sm">
                {error}
              </Text>
            )}

            <Button type="submit" variant="hydrologiq_primary" w="full" disabled={loading}>
              {loading ? "Sending..." : "Send Reset Link"}
            </Button>

            <Stack direction="row" justify="center" gap={1} fontSize="sm">
              <Text>Remember your password?</Text>

              <Link href="/auth/sign-in" variant={"primary" as any} internal>
                Sign in
              </Link>
            </Stack>
          </>
        )}
      </VStack>
    </Box>
  )
}
